"use client";

import React, { useCallback, useEffect, useState } from "react";
import DropdownSelectOne from "../../../components/custom/dropdown-select-one";
import DateInputCustom from "../../../components/custom/date-input/date-input-custom";
import EmptyState from "../../../components/custom/empty-state";
import { showToast } from "../../../components/custom/custom-toast";
import { useDebounce } from "../../../hooks/use-debounce";
import { formatDateTime } from "../../fault-ticket/services/fault-ticket-service";
import BankTransferService, {
  BANK_TRANSFER_MATCH_STATUS,
  BANK_TRANSFER_MATCH_STATUS_CLASS,
  BANK_TRANSFER_MATCH_STATUS_LABEL,
  BANK_TRANSFER_PENDING_STATUSES,
  BANK_TRANSFER_SOURCE_LABEL,
  BANK_TRANSFER_VOUCHER_STATUS,
  BankTransfer,
  BankTransferListResponse,
} from "../services/bank-transfer-service";
import BankTransferAssignModal from "../components/bank-transfer-assign-modal";

// dd/MM/yyyy (khớp DateInputCustom + BE TimeHelper)
const toDMY = (d: Date) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;

const money = (v: number | null | undefined) => Number(v || 0).toLocaleString("vi-VN");

// Thứ tự ô tổng hợp: việc cần xử lý trước, đã xong sau.
const SUMMARY_ORDER = [
  BANK_TRANSFER_MATCH_STATUS.UNMATCHED,
  BANK_TRANSFER_MATCH_STATUS.UNDERPAID,
  BANK_TRANSFER_MATCH_STATUS.NEEDS_REVIEW,
  BANK_TRANSFER_MATCH_STATUS.MATCHED,
  BANK_TRANSFER_MATCH_STATUS.IGNORED,
];

const BankTransferIndexPage: React.FC = () => {
  const { fetch: fetchList } = BankTransferService.list();
  const { fetch: ignoreTransfer } = BankTransferService.ignore();
  const { fetch: retryVoucher } = BankTransferService.retryVoucher();

  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [data, setData] = useState<BankTransferListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<number | "all">("all");
  const [fromDate, setFromDate] = useState<string>(toDMY(firstDayOfMonth));
  const [toDate, setToDate] = useState<string>(toDMY(today));
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 800);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [assigning, setAssigning] = useState<BankTransfer | null>(null);
  const [ignoringId, setIgnoringId] = useState<number | null>(null);
  const [retryingVoucherId, setRetryingVoucherId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetchList({
        match_status: status === "all" ? undefined : Number(status),
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        key_search: debouncedSearch || undefined,
        page,
        limit,
      });
      if (res?.status === 200 && res.data) {
        setData(res.data as BankTransferListResponse);
      } else if (res?.message) {
        showToast({ type: "error", message: res.message });
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, fromDate, toDate, debouncedSearch, page, limit]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    setPage(1);
  }, [status, fromDate, toDate, debouncedSearch, limit]);

  const totalRecords = data?.total_record ?? 0;
  const rows = data?.list ?? [];
  const totalPages = Math.max(1, Math.ceil(totalRecords / limit));
  const summaryMap = new Map((data?.summary ?? []).map((s) => [s.match_status, s]));

  const handleIgnore = async (t: BankTransfer) => {
    const note = window.prompt(
      `Bỏ qua khoản ${money(t.amount)} ₫ vì không thuộc đơn hàng nào?\n\nNhập lý do (có thể để trống):`,
      "",
    );
    if (note === null) return; // bấm Hủy
    setIgnoringId(t.id);
    try {
      const res = await ignoreTransfer(t.id, note.trim());
      if (res?.status === 200) {
        showToast({ type: "success", message: "Đã bỏ qua giao dịch" });
        await load();
      } else {
        showToast({ type: "error", message: res?.message || "Bỏ qua giao dịch thất bại" });
      }
    } finally {
      setIgnoringId(null);
    }
  };

  // Đơn đã được ghi nhận thanh toán nhưng bước tạo phiếu thu lỗi (vd accounting tạm ngừng) → tạo lại.
  const handleRetryVoucher = async (t: BankTransfer) => {
    setRetryingVoucherId(t.id);
    try {
      const res = await retryVoucher(t.id);
      if (res?.status === 200) {
        showToast({ type: "success", message: `Đã tạo phiếu thu cho đơn ${t.order_code || ""}` });
        await load();
      } else {
        showToast({ type: "error", message: res?.message || "Tạo phiếu thu thất bại" });
      }
    } finally {
      setRetryingVoucherId(null);
    }
  };

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Đối soát chuyển khoản</h1>
        <p className="text-sm text-slate-500 mt-1">
          Tiền vào tài khoản ngân hàng từ thanh toán QR và kết quả khớp với đơn hàng
        </p>
      </div>

      {/* Tổng hợp theo trạng thái (theo khoảng ngày + từ khoá, không theo bộ lọc trạng thái) — bấm để lọc */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {SUMMARY_ORDER.map((st) => {
          const s = summaryMap.get(st);
          const active = status === st;
          return (
            <button
              key={st}
              onClick={() => setStatus(active ? "all" : st)}
              className={`text-left bg-white rounded-xl border p-4 shadow-sm transition hover:border-green-400 ${
                active ? "border-green-500 ring-1 ring-green-500" : "border-slate-200"
              }`}
            >
              <span
                className={`px-2 py-0.5 text-xs font-semibold rounded-full ${BANK_TRANSFER_MATCH_STATUS_CLASS[st]}`}
              >
                {BANK_TRANSFER_MATCH_STATUS_LABEL[st]}
              </span>
              <div className="mt-2 text-xl font-bold text-slate-800">{s?.count ?? 0}</div>
              <div className="text-sm text-slate-500">{money(s?.amount)} ₫</div>
            </button>
          );
        })}
      </div>

      {/* Filter Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 p-4">
          <div className="min-w-[200px]">
            <DropdownSelectOne
              defaultOption={{ value: "all", label: "Tất cả trạng thái" }}
              options={Object.entries(BANK_TRANSFER_MATCH_STATUS_LABEL).map(([value, label]) => ({
                value: Number(value) as number | string,
                label,
              }))}
              selectedId={status === "all" ? "all" : status}
              onChange={(opt) => setStatus(opt.value === "all" ? "all" : Number(opt.value))}
              getOptionLabel={(o) => o.label}
              getOptionKey={(o) => o.value}
              placeholder="Trạng thái"
            />
          </div>

          <div className="flex items-center gap-2">
            {/* Nối chéo min/max để không chọn được khoảng ngược */}
            <DateInputCustom
              type="date"
              value={fromDate}
              maxDate={toDate || undefined}
              onChange={(d) => setFromDate(d ?? "")}
              placeholder="Từ ngày"
              className="w-[150px]"
            />
            <svg className="w-4 h-4 text-slate-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
            <DateInputCustom
              type="date"
              value={toDate}
              minDate={fromDate || undefined}
              onChange={(d) => setToDate(d ?? "")}
              placeholder="Đến ngày"
              className="w-[150px]"
            />
          </div>

          <div className="flex-1 relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Tìm nội dung chuyển khoản, người chuyển, mã giao dịch..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-x-auto overflow-y-auto max-h-[60vh] w-full scroll-smooth">
        {/* "Đang tải" / EmptyState nằm trong khung nhưng NGOÀI <table>: bảng rộng 1200px nên đặt trong ô colSpan
            sẽ bị căn giữa theo bề rộng bảng, lệch khỏi vùng nhìn thấy. */}
        {loading && rows.length === 0 ? (
          <div className="py-16 text-center text-sm text-slate-500">Đang tải dữ liệu...</div>
        ) : rows.length === 0 ? (
          <EmptyState
            message="Chưa có giao dịch chuyển khoản"
            description="Không có giao dịch nào khớp bộ lọc trong khoảng ngày đã chọn."
          />
        ) : (
        <table className="min-w-[1200px] w-full text-sm text-slate-700 border-collapse">
          <thead className="bg-slate-100 text-slate-600 text-xs uppercase sticky top-0 z-10 text-center whitespace-nowrap">
            <tr>
              <th className="px-4 py-3 border border-slate-200">STT</th>
              <th className="px-4 py-3 border border-slate-200">Thời gian GD</th>
              <th className="px-4 py-3 border border-slate-200">Số tiền</th>
              <th className="px-4 py-3 border border-slate-200">Nội dung chuyển khoản</th>
              <th className="px-4 py-3 border border-slate-200">Người chuyển</th>
              <th className="px-4 py-3 border border-slate-200">Đơn hàng</th>
              <th className="px-4 py-3 border border-slate-200">Trạng thái</th>
              <th className="px-4 py-3 border border-slate-200">Ghi chú</th>
              <th className="px-4 py-3 sticky right-0 bg-slate-100 z-30 shadow-md border border-slate-200">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t, idx) => {
                const pending = BANK_TRANSFER_PENDING_STATUSES.includes(t.match_status);
                return (
                  <tr key={t.id} className="border-t hover:bg-slate-50 transition group align-top">
                    <td className="px-4 py-3 border border-slate-200 text-center">
                      {(page - 1) * limit + idx + 1}
                    </td>
                    <td className="px-4 py-3 border border-slate-200 text-center whitespace-nowrap">
                      {/* Ưu tiên giờ giao dịch của ngân hàng; thiếu thì dùng giờ hệ thống nhận */}
                      <div>{formatDateTime(t.transaction_date || t.created_at)}</div>
                      <div className="text-[11px] text-slate-400 mt-0.5">
                        {t.bank_code} · {BANK_TRANSFER_SOURCE_LABEL[t.source] || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-3 border border-slate-200 text-right font-semibold whitespace-nowrap">
                      {money(t.amount)}
                    </td>
                    <td className="px-4 py-3 border border-slate-200 max-w-[280px] break-words">
                      <div>{t.content || "—"}</div>
                      {t.transaction_code && (
                        <div className="text-[11px] text-slate-400 mt-0.5">Mã GD: {t.transaction_code}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 border border-slate-200">
                      <div>{t.remitter_name || "—"}</div>
                      {(t.remitter_account_number || t.remitter_bank_name) && (
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {[t.remitter_account_number, t.remitter_bank_name].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border border-slate-200 text-center whitespace-nowrap">
                      {t.order_code ? (
                        <>
                          <div className="font-semibold text-slate-800">{t.order_code}</div>
                          {t.order_total_amount != null && (
                            <div className="text-[11px] text-slate-400 mt-0.5">
                              Tiền đơn: {money(t.order_total_amount)}
                            </div>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-4 py-3 text-center border border-slate-200">
                      <span
                        className={`px-2 py-1.5 text-xs font-semibold rounded-full whitespace-nowrap min-w-[110px] inline-block text-center ${
                          BANK_TRANSFER_MATCH_STATUS_CLASS[t.match_status] || "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {BANK_TRANSFER_MATCH_STATUS_LABEL[t.match_status] || "Không rõ"}
                      </span>
                      {/* Phiếu thu chỉ có ý nghĩa với giao dịch đã khớp đơn */}
                      {t.match_status === BANK_TRANSFER_MATCH_STATUS.MATCHED && (
                        <div
                          className={`mt-1 text-[11px] ${
                            t.voucher_status === BANK_TRANSFER_VOUCHER_STATUS.CREATED
                              ? "text-slate-400"
                              : "text-red-600 font-semibold"
                          }`}
                        >
                          {t.voucher_status === BANK_TRANSFER_VOUCHER_STATUS.CREATED
                            ? "Đã có phiếu thu"
                            : "Chưa có phiếu thu"}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 border border-slate-200 max-w-[260px] break-words text-slate-600">
                      {t.note || "—"}
                    </td>
                    <td className="px-4 py-3 text-center sticky right-0 bg-white group-hover:bg-slate-50 transition-colors border border-slate-200 whitespace-nowrap">
                      {pending ? (
                        <div className="flex justify-center gap-2">
                          <button
                            onClick={() => setAssigning(t)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-green-600 text-white hover:bg-green-700"
                          >
                            Gán đơn
                          </button>
                          <button
                            onClick={() => handleIgnore(t)}
                            disabled={ignoringId === t.id}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 disabled:opacity-50"
                          >
                            {ignoringId === t.id ? "Đang lưu..." : "Bỏ qua"}
                          </button>
                        </div>
                      ) : t.match_status === BANK_TRANSFER_MATCH_STATUS.MATCHED &&
                        t.voucher_status !== BANK_TRANSFER_VOUCHER_STATUS.CREATED ? (
                        <button
                          onClick={() => handleRetryVoucher(t)}
                          disabled={retryingVoucherId === t.id}
                          className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                        >
                          {retryingVoucherId === t.id ? "Đang tạo..." : "Tạo lại phiếu thu"}
                        </button>
                      ) : t.match_status === BANK_TRANSFER_MATCH_STATUS.IGNORED ? (
                        // Bỏ qua nhầm vẫn gán lại được cho đơn.
                        <button
                          onClick={() => setAssigning(t)}
                          className="text-xs text-green-600 hover:underline"
                        >
                          Gán đơn
                        </button>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
        <div>
          {totalRecords > 0
            ? `Hiển thị ${(page - 1) * limit + 1} đến ${(page - 1) * limit + rows.length} của ${totalRecords} hàng`
            : "Không có dữ liệu"}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded px-2 py-1"
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>

          {(() => {
            const pages: (number | string)[] = [];
            const add = (p: number | string) => pages.push(p);
            const start = Math.max(1, page - 1);
            const end = Math.min(totalPages, page + 1);
            add(1);
            if (start > 2) add("...");
            for (let p = start; p <= end; p++) if (p !== 1 && p !== totalPages) add(p);
            if (end < totalPages - 1) add("...");
            if (totalPages > 1) add(totalPages);
            return (
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  {"<"}
                </button>
                {pages.map((p, idx) =>
                  typeof p === "number" ? (
                    <button
                      key={idx}
                      onClick={() => setPage(p)}
                      className={`w-8 h-8 rounded-full ${
                        p === page
                          ? "bg-green-600 text-white"
                          : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                      }`}
                    >
                      {p}
                    </button>
                  ) : (
                    <span key={idx} className="px-2">
                      {p}
                    </span>
                  )
                )}
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  {">"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      <BankTransferAssignModal
        transfer={assigning}
        onClose={() => setAssigning(null)}
        onDone={() => load()}
      />
    </div>
  );
};

export default BankTransferIndexPage;
